import { html, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhDotsSixVertical";

import { WowSortableTable } from "./wow-sortable-table";
import type { TableColumn } from "./types";

export abstract class WowReorderableTable<T> extends WowSortableTable<T> {
  protected abstract onReorder(orderedItems: T[]): Promise<void>;

  @property({ type: Boolean, attribute: "reorder-mode", reflect: true })
  reorderMode = false;

  @state() private _draggedIndex: number | null = null;
  @state() private _dropTargetIndex: number | null = null;
  @state() private _dropSide: "above" | "below" = "below";

  @state() private _originalColumns: TableColumn<T>[] | null = null;

  private _activeDragTarget: HTMLElement | null = null;

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has("reorderMode") && this.reorderMode) {
      this.clearAllSorts();
    }

    super.willUpdate(changed);
  }

  override update(changed: Map<string, unknown>) {
    if (changed.has("reorderMode")) {
      if (this.reorderMode) {
        this._originalColumns = this.columns;
      } else if (this._originalColumns) {
        this.columns = this._originalColumns;
        this._originalColumns = null;
      }
    }

    if (this.reorderMode) {
      const baseColumns = this.columns.filter((col) => col.key !== "drag_handle");
      this.columns = [this.createDragHandleColumn(), ...baseColumns.map((col) => ({ ...col, sortable: false }))];
    }

    super.update(changed);
  }

  override updated(changed: Map<string, unknown>) {
    super.updated(changed);

    if (this.reorderMode) {
      this.applyDragAttributes();
    }
  }

  private clearAllSorts() {
    const sortState = this.getSortState();
    const activeSorts = sortState.activeSorts.get();
    for (const sort of activeSorts) {
      this.getSortService().updateSort(sort.column, "none");
    }
    this.requestUpdate();
  }

  private createDragHandleColumn(): TableColumn<T> {
    return {
      key: "drag_handle",
      header: "",
      render: (_value: unknown, _row: T, rowIndex: number) => this.renderDragHandle(rowIndex),
    };
  }

  protected renderDragHandle(rowIndex: number): TemplateResult {
    return html`
      <div class="drag-handle" @pointerdown=${(e: PointerEvent) => this.onDragStart(e, rowIndex)}>
        <ph-dots-six-vertical size="20"></ph-dots-six-vertical>
      </div>
    `;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._activeDragTarget) {
      this.cleanupListeners(this._activeDragTarget);
    }
    this.resetDragState();
  }

  private onDragStart(e: PointerEvent, rowIndex: number) {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);

    this._draggedIndex = rowIndex;
    this._activeDragTarget = target;

    target.addEventListener("pointermove", this.onDragMove);
    target.addEventListener("pointerup", this.onDragEnd);
    target.addEventListener("pointercancel", this.onDragCancel);
  }

  private getDragElements(): NodeListOf<Element> | undefined {
    const rows = this.shadowRoot?.querySelectorAll("tbody tr");
    if (rows && rows.length > 0) return rows;
    return this.shadowRoot?.querySelectorAll(".mobile-view > .tile") ?? undefined;
  }

  private onDragMove = (e: PointerEvent) => {
    if (this._draggedIndex === null) return;

    const rows = this.getDragElements();
    if (!rows) return;

    for (let i = 0; i < rows.length; i++) {
      if (i === this._draggedIndex) continue;

      const row = rows[i];
      if (!row) continue;
      const rect = row.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;

      if (e.clientY >= rect.top && e.clientY <= rect.bottom) {
        this._dropTargetIndex = i;
        this._dropSide = e.clientY < midY ? "above" : "below";
        return;
      }
    }
  };

  private onDragEnd = (e: PointerEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.releasePointerCapture(e.pointerId);
    this.cleanupListeners(target);

    if (this._draggedIndex !== null && this._dropTargetIndex !== null) {
      this.performReorder();
    } else {
      this.resetDragState();
    }
  };

  private onDragCancel = (e: PointerEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.releasePointerCapture(e.pointerId);
    this.cleanupListeners(target);
    this.resetDragState();
  };

  private cleanupListeners(target: HTMLElement) {
    target.removeEventListener("pointermove", this.onDragMove);
    target.removeEventListener("pointerup", this.onDragEnd);
    target.removeEventListener("pointercancel", this.onDragCancel);
    this._activeDragTarget = null;
  }

  private async performReorder() {
    if (this._draggedIndex === null || this._dropTargetIndex === null) {
      this.resetDragState();
      return;
    }

    const items = [...this.data];
    const draggedItem = items.splice(this._draggedIndex, 1)[0];

    if (!draggedItem) {
      this.resetDragState();
      return;
    }

    let insertIndex = this._dropTargetIndex;
    if (this._draggedIndex < this._dropTargetIndex) {
      insertIndex = this._dropSide === "below" ? this._dropTargetIndex : this._dropTargetIndex - 1;
    } else {
      insertIndex = this._dropSide === "below" ? this._dropTargetIndex + 1 : this._dropTargetIndex;
    }

    items.splice(insertIndex, 0, draggedItem);

    this.resetDragState();

    try {
      await this.onReorder(items);
      this.emit("reorder-complete", { items });
    } catch (error) {
      this.emit("reorder-failed", { error });
    }
  }

  private resetDragState() {
    this._draggedIndex = null;
    this._dropTargetIndex = null;
    this.clearDragAttributes();
  }

  private applyDragAttributes() {
    const rows = this.getDragElements();
    if (!rows) return;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] as HTMLElement;

      row.toggleAttribute("dragging", i === this._draggedIndex);
      row.toggleAttribute(
        "drop-above",
        i === this._dropTargetIndex && this._dropSide === "above" && i !== this._draggedIndex,
      );
      row.toggleAttribute(
        "drop-below",
        i === this._dropTargetIndex && this._dropSide === "below" && i !== this._draggedIndex,
      );
    }
  }

  private clearDragAttributes() {
    const rows = this.getDragElements();
    if (!rows) return;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] as HTMLElement;
      row.removeAttribute("dragging");
      row.removeAttribute("drop-above");
      row.removeAttribute("drop-below");
    }
  }
}
